import { z } from "zod";
import { entityBaseSchema, slugSchema } from "./common.js";

/**
 * Scenario frontmatter — concrete outline + persona binding (runnable).
 *
 * Body markdown holds the persona-specific first-person brief.
 * Mirrors `scenarios` table in cloud schema.
 */
export const scenarioFrontmatterSchema = entityBaseSchema.extend({
  /** Slug ref to an entity in `iamu/outlines/`. */
  outline: slugSchema,
  /**
   * Slug ref to an entity in `iamu/personas/`. Nullish signals a
   * draft — a scenario without a persona binding is not runnable;
   * `/iamu:run` surfaces drafts under a "needs persona" group and
   * prompts for bind before execution. Cloud push for a null-persona
   * scenario is allowed but findings against a draft scenario can't
   * round-trip (findings require persona_id NOT NULL on cloud).
   * See iamu-skill-surface-minimal Decision 10.
   */
  persona: slugSchema.nullish(),
  /** Default target URL for walkthroughs; null when unknown at scenario-create time. */
  target_url: z.string().url().nullish(),
  /** Where the situation starts, as a path on the product (e.g. `/en/episodes/1`). Protocol-relative paths are forbidden. */
  entry_path: z
    .string()
    .regex(
      /^\/(?!\/)[^\s]*$/,
      "must be a path starting with a single / (protocol-relative URLs forbidden)",
    )
    .max(2048)
    .nullish(),
  /** Opt-in review passes to run after walkthrough completes. Empty = none (backward compatible). */
  reviews: z.array(z.string().min(1).max(64)).max(16).default([]),
  baseline_score: z.number().min(0).max(100).nullish(),
  current_score: z.number().min(0).max(100).nullish(),
  target_score: z.number().min(0).max(100).default(95),
  /** Environment state preconditions and approximation guidance. */
  state: z
    .object({
      /** Description of data/records expected to exist in the environment. */
      description: z.string().max(2048).nullish(),
      /** Guidance on how to squint or approximate if exact records are absent. */
      approximation: z.string().max(2048).nullish(),
    })
    .nullish(),
});

export type ScenarioState = NonNullable<ScenarioFrontmatter["state"]>;
export type ScenarioFrontmatter = z.infer<typeof scenarioFrontmatterSchema>;
